import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_HOLIDAY_SET } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useNow from "@/utils/hooks/useNow";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { createArray, toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viCalendar, viGear } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Calendar from "./calendar/Calendar";
import holidaySetReducer from "./holidaySetReducer";

function HolidaySet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const now = useNow();
  const currentYear = now.getFullYear();

  const pbx = useSelector((state) => state.domain.pbx);

  const [tab, setTab] = useState(() => (id === "new" ? "settings" : currentYear));
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setTab(id === "new" ? "settings" : currentYear);
  }

  const viewDate = useMemo(() => new Date(now).setFullYear(tab), [now, tab]);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getSounds());
  }, []);

  const {
    value: holidaySet,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_HOLIDAY_SET,
    reducer: holidaySetReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
    }),
    onSubmit: () => Api.storeHolidaySet(holidaySet),
    onSuccess: (response) => {
      if (!holidaySet.id) {
        dispatch({ type: "id", value: response.data.holidaySet.id });
        navigate(`../${response.data.holidaySet.id}`);
      }

      Promise.resolve(Api.getHolidaySets());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === holidaySet.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_HOLIDAY_SET });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getHolidaySet({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          setTab(currentYear);
          dispatch({ type: "set", value: response.data.holidaySet });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [currentYear, dispatch, holidaySet.id, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getHolidaySets()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${holidaySet.name || "Holiday set"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={(e, _tab) => setTab(_tab)}
          tabs={[
            {
              label: "Settings",
              icon: viGear,
              id: "settings",
              value: "settings",
            },
            ...createArray(currentYear, 4).map((year) => ({
              label: year,
              icon: viCalendar,
              id: year,
              disabled: id === "new",
              value: year,
              disableResponsive: true,
            })),
          ]}
          disableResponsiveTabs
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={holidaySet.name} icon={viCalendar} />
              </IconButton>
            ) : (
              <Avatar text={holidaySet.name} icon={viCalendar} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={holidaySet.id || "nk"}
                isActionable={!!holidaySet?.id}
                onDelete={() => Api.deleteHolidaySet(holidaySet)}
                name={`${holidaySet.name}`}
                deleteInfo="Are you sure you want to delete this holiday set?"
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={holidaySet.name || "Unnamed holiday set"}
        />
        {tab === "settings" && (
          <FormGroup title="Identity">
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputText
                required
                id="name"
                label="Name"
                onChange={formHandler}
                value={holidaySet.name}
                error={errors}
              />
            </Grid>
          </FormGroup>
        )}
        {tab !== "settings" && <Calendar viewDate={viewDate} holidaySet={holidaySet} holidaySetDispatch={dispatch} />}
      </Stack>
    </Container>
  );
}

export default HolidaySet;
